import React from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { PWA } from "@/constants/testIds";
import { LOGO_URL } from "./data";
import Reveal from "./Reveal";

const HERO_BG =
  "https://images.pexels.com/photos/36887744/pexels-photo-36887744.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=2000";

const Hero = () => {
  const go = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid={PWA.heroSection}
      className="relative min-h-screen w-full overflow-hidden bg-[#0c0c0c] noise-overlay"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Craftsman at workbench"
          className="w-full h-full object-cover kenburns opacity-90"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/85" />
      <div className="absolute inset-0 wood-grain-dark opacity-40" />

      <div className="relative z-10 min-h-screen w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-20 md:pb-28 pt-32">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="flex items-center gap-4 mb-7">
                <span className="block w-px h-10 bg-[#C19A6B]" />
                <span className="text-[11px] tracking-[0.4em] uppercase text-[#C19A6B] font-semibold">
                  Est 2018 · South Florida
                </span>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <h1
                data-testid={PWA.heroHeadline}
                className="font-serif-display text-[#FAF9F6] text-5xl sm:text-6xl md:text-7xl lg:text-[88px] xl:text-[104px] leading-[0.95] tracking-tighter font-medium"
              >
                Custom Woodworking
                <br />
                <span className="italic font-normal text-[#D9B27F]">Built With Precision.</span>
              </h1>
            </Reveal>

            <Reveal delay={260}>
              <p className="mt-8 max-w-2xl text-[#FAF9F6]/80 text-lg md:text-xl font-light leading-relaxed">
                Handcrafted furniture, cabinetry, built-ins, and one-of-a-kind wood pieces — made
                with care, detail, and the kind of artistry you can feel the moment you walk into
                the room.
              </p>
            </Reveal>

            <Reveal delay={400}>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  data-testid={PWA.heroCtaQuote}
                  onClick={() => go("#quote")}
                  className="btn-gold"
                >
                  Request a Custom Quote
                  <ArrowUpRight className="w-4 h-4" />
                </button>
                <button
                  data-testid={PWA.heroCtaWork}
                  onClick={() => go("#gallery")}
                  className="btn-outline-light"
                >
                  View Our Work
                </button>
              </div>
            </Reveal>

            <Reveal delay={520}>
              <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] tracking-[0.32em] uppercase text-[#FAF9F6]/70">
                <span>Established 2018</span>
                <span className="text-[#C19A6B]">·</span>
                <span>Custom Builds</span>
                <span className="text-[#C19A6B]">·</span>
                <span>Local Craftsmanship</span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-4 hidden lg:flex flex-col items-end gap-6">
            <Reveal delay={300} className="w-full">
              <div className="ml-auto w-full max-w-[320px] bg-[#FAF9F6]/5 backdrop-blur-md border border-[#C19A6B]/25 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden ring-1 ring-[#C19A6B]/40 bg-white shrink-0">
                    <img src={LOGO_URL} alt="logo" data-testid={PWA.heroLogo} className="w-full h-full object-cover" />
                  </div>
                  <div className="leading-tight">
                    <p className="font-serif-display text-[#FAF9F6] text-xl">Precision</p>
                    <p className="text-[10px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold">
                      Woodworking Artistry
                    </p>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
                  <div className="flex flex-col items-center">
                    <p className="font-serif-display text-[#D9B27F] text-2xl leading-none">15+</p>
                    <p className="text-[9px] tracking-[0.18em] uppercase text-[#FAF9F6]/60 mt-2 whitespace-nowrap">
                      Years
                    </p>
                  </div>
                  <div className="flex flex-col items-center border-x border-white/10 px-2">
                    <p className="font-serif-display text-[#D9B27F] text-2xl leading-none">300+</p>
                    <p className="text-[9px] tracking-[0.18em] uppercase text-[#FAF9F6]/60 mt-2 whitespace-nowrap">
                      Builds
                    </p>
                  </div>
                  <div className="flex flex-col items-center">
                    <p className="font-serif-display text-[#D9B27F] text-2xl leading-none">5★</p>
                    <p className="text-[9px] tracking-[0.18em] uppercase text-[#FAF9F6]/60 mt-2 whitespace-nowrap">
                      Rated
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <button
          onClick={() => go("#about")}
          aria-label="Scroll down"
          className="hidden md:flex absolute bottom-8 right-8 lg:right-14 items-center gap-3 text-[#FAF9F6]/70 hover:text-[#C19A6B] transition-colors"
        >
          <span className="text-[10px] tracking-[0.32em] uppercase">Scroll</span>
          <ArrowDown className="w-4 h-4 animate-bounce" />
        </button>
      </div>
    </section>
  );
};

export default Hero;
